import React from 'react';

import { Point2d } from '../../viewer/utilities/math-helpers';

type Func = (pos?: Point2d) => React.ReactNode;

/**
 * A hook to handle rendering of dialogs.
 * Returns an object with functions to show, hide and render the dialog.
 */
export const useDialog = (defaultVisible = false) => {
  const [visible, setVisible] = React.useState(defaultVisible);
  const [pos, setPos] = React.useState<Point2d>();

  const show = React.useCallback((pos?: Point2d) => {
    setVisible(true);
    setPos(pos);
  }, []);

  const hide = React.useCallback(() => {
    setVisible(false);
  }, []);

  const render = React.useCallback(
    (funcOrNode: Func | React.ReactNode) => {
      if (visible) {
        return typeof funcOrNode === 'function' ? funcOrNode(pos) : funcOrNode;
      }
    },
    [pos, visible],
  );

  return {
    /**
     * Renders the specified dialog if it's currently visible.
     */
    render,

    /**
     * Sets visibility to true. Makes the render function add the dialog into the DOM.
     */
    show,

    /**
     * Sets visibility to false. Makes the render function remove dialog from DOM.
     */
    hide,

    /**
     * Indicates whether the dialog is currently visible.
     */
    visible,
  };
};
